import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Wallet, ArrowRight, CarFront, Lock, Clock } from "lucide-react";
import { photoSrc } from "@/lib/api";
import { STATUS_LABELS, STATUS_STYLES } from "@/data/categories";
import RequestModal from "@/components/RequestModal";
import SalePrice from "@/components/SalePrice";
import BenefitBadge from "@/components/BenefitBadge";
import { useBenefits, benefitsFor } from "@/hooks/useBenefits";

export default function VehicleCard({ vehicle, category }) {
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const benefits = benefitsFor(useBenefits(), { categoryId: vehicle.category_id, vehicleId: vehicle.id });
  const cover = vehicle.photos?.[0];
  const clickable = vehicle.status === "available" || vehicle.status === "booked";
  const strong = vehicle.status === "rented" || vehicle.status === "service";

  const badge = (
    <span
      data-testid={`vehicle-status-${vehicle.id}`}
      className={`absolute top-3 left-3 px-3 py-1 rounded-full font-semibold border backdrop-blur ${
        strong ? "text-sm px-4 py-1.5" : "text-xs"
      } ${STATUS_STYLES[vehicle.status]}`}
    >
      {STATUS_LABELS[vehicle.status]}
    </span>
  );

  const inner = (
    <>
      <div className="relative h-48 overflow-hidden bg-[#0D0F14]">
        {cover ? (
          <img
            src={photoSrc(cover)}
            alt={`${category.name} ${vehicle.year} ქირით ტაქსისთვის — taxipark.ge`}
            className={`w-full h-full object-cover ${strong ? "opacity-60 grayscale-[30%]" : ""}`}
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-gray-600">
            <CarFront className="w-9 h-9" />
            <span className="text-xs">ფოტო მალე დაემატება</span>
          </div>
        )}
        {badge}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <h3 className="text-lg font-semibold text-white">
          {vehicle.public_title || <>{category.name} <span className="text-[#E5C378]">{vehicle.year}</span></>}
        </h3>
        {vehicle.fuel_type && <p className="text-xs text-gray-500 mt-1">{vehicle.fuel_type}</p>}
        {vehicle.description && (
          <p className="text-gray-400 text-sm mt-2 leading-relaxed line-clamp-2">{vehicle.description}</p>
        )}
        {benefits.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3" data-testid={`vehicle-benefits-${vehicle.id}`}>
            {benefits.map((b) => (
              <BenefitBadge key={b.id} benefit={b} />
            ))}
          </div>
        )}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
          <SalePrice original={`${vehicle.price} ₾`} sale={vehicle.sale_price ? `${vehicle.sale_price} ₾` : null} enabled={vehicle.sale_enabled} />
          <p className="text-xs text-gray-500 flex items-center gap-1 shrink-0">
            <Wallet className="w-3.5 h-3.5" /> {vehicle.deposit} ₾
          </p>
        </div>
        {clickable ? (
          <span className="btn-gold w-full mt-5 !py-3 text-sm">
            დეტალურად <ArrowRight className="w-4 h-4" />
          </span>
        ) : vehicle.status === "rented" ? (
          <span
            data-testid={`vehicle-locked-${vehicle.id}`}
            className="mt-5 w-full inline-flex items-center justify-center gap-2 py-2 rounded-lg border border-white/10 text-gray-500 text-xs font-medium"
          >
            <Lock className="w-3.5 h-3.5" /> {STATUS_LABELS[vehicle.status]}
          </span>
        ) : (
          <span
            data-testid={`vehicle-locked-${vehicle.id}`}
            className="btn-ghost w-full mt-5 !py-3 text-sm opacity-50 cursor-not-allowed"
          >
            <Lock className="w-4 h-4" /> {STATUS_LABELS[vehicle.status]}
          </span>
        )}
      </div>
    </>
  );

  return (
    <motion.article
      data-testid={`vehicle-card-${vehicle.id}`}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      className="card-lux overflow-hidden flex flex-col"
    >
      {clickable ? (
        <Link to={`/vehicle/${vehicle.id}`} data-testid={`vehicle-cta-${vehicle.id}`} className="flex flex-col flex-1">
          {inner}
        </Link>
      ) : vehicle.status === "rented" ? (
        <div className="flex flex-col flex-1" aria-disabled="true">
          <div data-testid={`vehicle-cta-${vehicle.id}`} className="flex flex-col flex-1 cursor-default">{inner}</div>
          <div className="px-5 pb-5 relative z-10">
            <button
              onClick={() => setWaitlistOpen(true)}
              data-testid={`vehicle-waitlist-btn-${vehicle.id}`}
              className="btn-gold w-full !py-3 text-sm"
            >
              <Clock className="w-4 h-4" /> რიგში ჩადგომა
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col flex-1" data-testid={`vehicle-cta-${vehicle.id}`} aria-disabled="true">
          {inner}
        </div>
      )}
      {waitlistOpen && (
        <RequestModal type="waitlist" vehicle={vehicle} category={category} onClose={() => setWaitlistOpen(false)} />
      )}
    </motion.article>
  );
}
